import React, { useState, useEffect, useRef } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import _ from "lodash";

// Constants
const grid = 8;

// Functions
const getItemStyle = (isDragging, draggableStyle) => ({
  userSelect: "none",
  padding: grid,
  margin: `0 0 ${grid}px 0`,
  // change background colour if dragging
  background: isDragging ? "#337ab7" : "#b5b5b5",
  border: "1px solid #767676",
  borderRadius: "25px",
  // styles we need to apply on draggables
  ...draggableStyle,
});

const getListStyle = (isDraggingOver, ind) => ({
  background: isDraggingOver ? "black" : "#dddddd",
  border: `${ind}px dashed black`,
  padding: grid,
  width: "100%",
  overflowY: "scroll",
});

const getItems = (values) => {
  const result = [];
  for (let i = 0; i < values.length; i++) {
    result.push({
      id: values[i]._id,
      content: values[i].name,
    });
  }
  return result;
};

// Reorders an item in a list.
const handleReorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

// Moves an item from one list to another list.
const handleMove = (
  source,
  destination,
  droppableSource,
  droppableDestination
) => {
  const sourceClone = Array.from(source);
  const destClone = Array.from(destination);
  const [removed] = sourceClone.splice(droppableSource.index, 1);

  destClone.splice(droppableDestination.index, 0, removed);

  const result = {};
  result[droppableSource.droppableId] = sourceClone;
  result[droppableDestination.droppableId] = destClone;

  return result;
};

// Main Component Function
const DnDList = ({ style, options, path, onChange, name }) => {
  // ComponentDidMount/Update Function
  function usePrevious(value) {
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  }

  // Component Initialization
  const [state, setState] = useState([getItems(options), getItems(path)]);

  // When Props Change
  const prevOptions = usePrevious(options);
  const prevPath = usePrevious(path);
  useEffect(() => {
    if (prevOptions !== options || prevPath !== path) {
      // Remove Options which are already in Current Path
      const allOptions = [options, path];
      const filteredOptions = _.differenceWith(...allOptions, _.isEqual);

      setState([getItems(filteredOptions), getItems(path)]);
    }
  }, [prevOptions, prevPath, path, options]);

  // Drag Event Handler Function
  function onDragEnd(result) {
    const { source, destination } = result;

    // dropped outside the list
    if (!destination) {
      return;
    }
    const sInd = +source.droppableId;
    const dInd = +destination.droppableId;

    if (sInd === dInd) {
      const items = handleReorder(state[sInd], source.index, destination.index);
      // Send Data to Form
      onChange(items, name);

      const newState = [...state];
      newState[sInd] = items;
      setState(newState);
    } else {
      const result = handleMove(state[sInd], state[dInd], source, destination);
      // Send Data to Form
      onChange(result["1"], name);

      const newState = [...state];
      newState[sInd] = result[sInd];
      newState[dInd] = result[dInd];

      setState(newState.filter((group) => group.length));
    }
  }

  return (
    <div style={style}>
      <DragDropContext onDragEnd={onDragEnd}>
        {state.map((el, ind) => (
          <Droppable key={ind} droppableId={`${ind}`}>
            {(provided, snapshot) => (
              <div
                ref={provided.innerRef}
                style={getListStyle(snapshot.isDraggingOver, ind)}
                {...provided.droppableProps}
              >
                {el.map((item, index) => (
                  <Draggable key={item.id} draggableId={item.id} index={index}>
                    {(provided, snapshot) => (
                      <div
                        className="row align-items-center"
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={getItemStyle(
                          snapshot.isDragging,
                          provided.draggableProps.style
                        )}
                      >
                        <div className="col-9">{item.content}</div>
                        <div className="col-3 p-1">
                          <button
                            style={
                              ind
                                ? { borderColor: "white" }
                                : { display: "none" }
                            }
                            className="btn btn-sm btn-danger btn-circle rounded"
                            type="button"
                            onClick={() => {
                              const newState = [...state];
                              newState[ind].splice(index, 1);
                              // Send Data to Form
                              onChange(newState[1], name);
                              setState(
                                newState.filter((group) => group.length)
                              );
                            }}
                          >
                            <i
                              className="fa fa-times round-button "
                              aria-hidden="true"
                            ></i>
                          </button>
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        ))}
      </DragDropContext>
    </div>
  );
};

export default DnDList;
